// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { IDevice, ILicense } from 'src/types/subscription';
import {
    IClientPolicyDefaults,
    ID3EPolicy, IFeatureMaskOption, IReAuthFrequency,
    ISensor,
    ISensorType,
    ransomwareFeatureMaskValues, validReAuthFrequencyValues,
    validTimeoutValues
} from 'src/types/d3ePolicy';
import { AuthService } from 'src/services/auth-service';
import { D3eService } from 'src/services/d3e-service';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../../services/notification-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import authHelper from '../../../auth/authHelper';
import { MatRipple } from '@angular/material/core';


@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-policy-settings',
    templateUrl: './policy-settings.component.html',
    styleUrls: ['./policy-settings.component.scss'],
    standalone: false
})
export class PolicySettingsComponent implements OnInit, OnChanges, AfterViewInit {
  @Input()haveDDTier: boolean;
  @Input()devices: ILicense[] = [];
  @Input()d3ePolicy: ID3EPolicy;
  @Input()forcePolicyRemoval: boolean;
  @Input()allowAlwaysOn: boolean;
  @Input()show_force_policy_checkbox: boolean;
  @Input()clientPolicyDefaults: IClientPolicyDefaults;
  @Output()loadDeviceSettings: EventEmitter<ILicense> = new EventEmitter<ILicense>();
  @Output()forcePolicyRemovalChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()intervalsChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();
  @Output()sensorToggleChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();
  @Output()allowAlwaysOnChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()forcePreAuthChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();
  @Output()preAuthFileAccessCountChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();
  @Output()preAuthFileAccessTimeoutChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();
  @Output()ransomwareSensitivityMinChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();
  @Output()ransomwareSensitivityMaxChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();
  @Output()ransomwareFeatureMaskChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();


  @ViewChild('alwaysOnToggleElement') ref: MatSlideToggle;
  @ViewChild(MatRipple, {static: false}) ripple: MatRipple;

  _d3ePolicy: ID3EPolicy;
  defaultPolicy: ID3EPolicy;
  syncIntervalUnit: 'secs' | 'mins' | 'hrs' = 'secs';
  syncIntervalMeteredUnit: 'mins' | 'hrs' = 'mins';
  syncIntervalMin: number;
  syncIntervalMax: number;
  syncIntervalDefault: number;
  syncIntervalRecommended: number;
  syncIntervalMeteredMin: number;
  syncIntervalMeteredMax: number;
  syncIntervalMeteredDefault: number
  syncIntervalMeteredRecommended: number;
  licenseIntervalMin: number;
  licenseIntervalMax: number;
  licenseIntervalDefault: number;
  licenseIntervalRecommended: number;
  licenseIntervalMeteredMin: number;
  licenseIntervalMeteredMax: number;
  licenseIntervalMeteredDefault: number;
  licenseIntervalMeteredRecommended: number;
  selectedDevice: ILicense;
  sensorTypes: ISensorType[] = [];
  forcePreAuthValuesMin: number;
  forcePreAuthValuesMax: number;
  forcePreAuthValuesDefault: validReAuthFrequencyValues;
  forcePreAuthValuesRecommended: number;
  preAuthFileAccessCountMin: number;
  preAuthFileAccessCountMax: number;
  preAuthFileAccessCountDefault: number;
  preAuthFileAccessCountRecommended: number;
  preAuthFileAccessTimeoutMin: validTimeoutValues;
  preAuthFileAccessTimeoutMax: validTimeoutValues;
  preAuthFileAccessTimeoutDefault: validTimeoutValues;
  preAuthFileAccessTimeoutRecommended: validTimeoutValues;

  ransomwareSensitivityMinMin: number;
  ransomwareSensitivityMinMax: number;
  ransomwareSensitivityMinDefault: number;
  ransomwareSensitivityMinRecommended: number;

  ransomwareSensitivityMaxMin: number;
  ransomwareSensitivityMaxMax: number;
  ransomwareSensitivityMaxDefault: number;
  ransomwareSensitivityMaxRecommended: number;

  ransomwareFeatureMaskMin: ransomwareFeatureMaskValues;
  ransomwareFeatureMaskMax: ransomwareFeatureMaskValues;
  ransomwareFeatureMaskDefault: ransomwareFeatureMaskValues;
  ransomwareFeatureMaskRecommended: ransomwareFeatureMaskValues;

  seconds = Array.from(new Array(31), (x, i) => i + 30);
  minutes = Array.from(new Array(60), (x, i) => i + 1);
  hours = Array.from(new Array(12), (x, i) => i + 1);
  timeUnits = ['secs', 'mins', 'hrs'];
  timeValues = this.seconds;

  minutesMetered = Array.from(new Array(60), (x, i) => i + 1);
  hoursMetered = Array.from(new Array(12), (x, i) => i + 1);
  timeUnitsMetered = ['mins', 'hrs'];
  timeValuesMetered = this.minutesMetered;

  // Feature flag to show/hide pre-auth file access count controls
  showFileAccessCount: boolean;
  showRansomware: boolean;

  DISPLAY_SENSORS = [];

  ransomware_mask_options: IFeatureMaskOption[] = [
    {'value': 1, 'label': this.translate.instant('policy.ransomware.feature-mask.report')},
    {'value': 3, 'label': this.translate.instant('policy.ransomware.feature-mask.all')},
  ];

  reAuthFrequencyOptions: IReAuthFrequency[] = [
    {'value': 1, 'label': this.translate.instant('policy.settings.reauth-frequency.fixed')},
    {'value': 0, 'label': this.translate.instant('policy.settings.reauth-frequency.maximum')}
  ]

  selectedFileFrequency: number;

  selectedTimeout: number;

  selectedFeatureMask: ransomwareFeatureMaskValues;

  selectedReAuthFrequency: validReAuthFrequencyValues;

  inputLabelStyles = {
    'min-width': '68px',
    'display': 'inline-block',
    'text-align': 'left',
  };
  inputFrequencyLabelStyles = {
    'min-width': '64px',
    'display': 'inline-block',
    'text-align': 'left',
  }
  minLabelStyles = {
    'min-width': '15px',
  };
  maxLabelStyles = {
    'min-width': '22px',
    'text-align': 'right'
  };
  inputStyles = {
    'min-width': '44px'
  };

  fUnknownError: string;

  get ransomwareSensitivityMin(): number {
    return this._d3ePolicy.ransomwareSensitivityMin;
  }

  set ransomwareSensitivityMin(value: number) {
    this._d3ePolicy.ransomwareSensitivityMin = value;
  }

  get ransomwareSensitivityMax(): number {
    return this._d3ePolicy.ransomwareSensitivityMax;
  }

  set ransomwareSensitivityMax(value: number) {
    this._d3ePolicy.ransomwareSensitivityMax = value;
    this.cd.detectChanges();
  }

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private d3eService: D3eService,
    private translate: TranslateService,
    private notification: NotificationService,
    private readonly cd: ChangeDetectorRef,
    ) {
    this.forcePolicyRemoval = false;
    this.allowAlwaysOn = false;
    this.show_force_policy_checkbox = false;

    this.translate.get(['ERROR_OCCURRED_UNKNOWN']).subscribe(vals => {
      this.fUnknownError = vals['ERROR_OCCURRED_UNKNOWN'];
    });

    this.showFileAccessCount = authHelper.getFeatureFlagFromToken('fac') ?? false;
    this.showRansomware = authHelper.getFeatureFlagFromToken('ran') ?? true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.forcePolicyRemoval) {
      this.forcePolicyRemoval = !!changes.forcePolicyRemoval.currentValue;
    }

    if (changes.allowAlwaysOn) {
      this.allowAlwaysOn = !!changes.allowAlwaysOn.currentValue;
    }

    if (changes?.d3ePolicy?.currentValue) {
      this.updatePolicy(changes.d3ePolicy.currentValue);
      // The following is a total hack so the ransomware sensitivity range slider
      // will reset the min if the max is set to less than the old min
      const min = this._d3ePolicy.ransomwareSensitivityMin;
      const max = this._d3ePolicy.ransomwareSensitivityMax;
      this.ransomwareSensitivityMin = 1;
      this.ransomwareSensitivityMax = 100;
      this.cd.detectChanges();
      this.ransomwareSensitivityMin = min;
      this.ransomwareSensitivityMax = max;
      this.cd.detectChanges();
      //  end of hack
    }
  }

  ngOnInit() {
    const settings = this.clientPolicyDefaults.sections.find(s => s.name.toLocaleLowerCase() === 'settings').groups[0].settings;
    const syncInterval = settings.find(s => s.name.toLowerCase() === 'syncinterval');
    this.syncIntervalDefault = this.convertFromTimeUnits(syncInterval.value, 'secs');
    this.syncIntervalMin = this.convertFromTimeUnits(syncInterval.min, 'secs');
    this.syncIntervalMax = this.convertFromTimeUnits(syncInterval.max, 'secs');
    this.syncIntervalRecommended = syncInterval.policyValue;

    const syncIntervalMetered = settings.find(s => s.name.toLowerCase() === 'syncintervalmetered');
    this.syncIntervalMeteredDefault = this.convertFromTimeUnits(syncIntervalMetered.value, 'mins');
    this.syncIntervalMeteredMin = this.convertFromTimeUnits(syncIntervalMetered.min, 'mins');
    this.syncIntervalMeteredMax = this.convertFromTimeUnits(syncIntervalMetered.max, 'mins');
    this.syncIntervalMeteredRecommended = syncIntervalMetered.policyValue;

    const licenseInterval = settings.find(s => s.name.toLowerCase() === 'licenseinterval');
    this.licenseIntervalDefault = this.convertFromTimeUnits(licenseInterval.value, 'hrs');
    this.licenseIntervalMin = this.convertFromTimeUnits(licenseInterval.min, 'hrs');
    this.licenseIntervalMax = this.convertFromTimeUnits(licenseInterval.max, 'hrs');
    this.licenseIntervalRecommended = licenseInterval.policyValue;

    const licenseIntervalMetered = settings.find(s => s.name.toLowerCase() === 'licenseintervalmetered');
    this.licenseIntervalMeteredDefault = this.convertFromTimeUnits(licenseIntervalMetered.value, 'hrs');
    this.licenseIntervalMeteredMin = this.convertFromTimeUnits(licenseIntervalMetered.min, 'hrs');
    this.licenseIntervalMeteredMax = this.convertFromTimeUnits(licenseIntervalMetered.max, 'hrs');
    this.licenseIntervalMeteredRecommended = licenseIntervalMetered.policyValue;

    const forcePreAuthValues = settings.find(s => s.name.toLowerCase() === 'forcepreauthvalues');
    this.forcePreAuthValuesDefault = forcePreAuthValues.value as validReAuthFrequencyValues;
    this.forcePreAuthValuesMin = forcePreAuthValues.min as validReAuthFrequencyValues;
    this.forcePreAuthValuesMax = forcePreAuthValues.max as validReAuthFrequencyValues;
    this.forcePreAuthValuesRecommended = forcePreAuthValues.policyValue as validReAuthFrequencyValues;

    const preAuthFileAccessCount = settings.find(s => s.name.toLowerCase() === 'preauthfileaccesscount');
    this.preAuthFileAccessCountDefault = preAuthFileAccessCount.value;
    this.preAuthFileAccessCountMin = preAuthFileAccessCount.min;
    this.preAuthFileAccessCountMax = preAuthFileAccessCount.max;
    this.preAuthFileAccessCountRecommended = preAuthFileAccessCount.policyValue;

    const preAuthFileAccessTimeout = settings.find(s => s.name.toLowerCase() === 'preauthfileaccesstimeout');
    this.preAuthFileAccessTimeoutDefault = preAuthFileAccessTimeout.value as validTimeoutValues;
    this.preAuthFileAccessTimeoutMin = preAuthFileAccessTimeout.min as validTimeoutValues;
    this.preAuthFileAccessTimeoutMax = preAuthFileAccessTimeout.max as validTimeoutValues;
    this.preAuthFileAccessTimeoutRecommended = preAuthFileAccessTimeout.policyValue as validTimeoutValues;

    const ransomwareSensitivityMin = settings.find(s => s.name.toLowerCase() === 'ransomwaresensitivitymin');
    this.ransomwareSensitivityMinDefault = ransomwareSensitivityMin.value as number;
    this.ransomwareSensitivityMinMin = ransomwareSensitivityMin.min as number;
    this.ransomwareSensitivityMinMax = ransomwareSensitivityMin.max as number;
    this.ransomwareSensitivityMinRecommended = ransomwareSensitivityMin.policyValue as number;

    const ransomwareSensitivityMax = settings.find(s => s.name.toLowerCase() === 'ransomwaresensitivitymax');
    this.ransomwareSensitivityMaxDefault = ransomwareSensitivityMax.value as number;
    this.ransomwareSensitivityMaxMin = ransomwareSensitivityMax.min as number;
    this.ransomwareSensitivityMaxMax = ransomwareSensitivityMax.max as number;
    this.ransomwareSensitivityMaxRecommended = ransomwareSensitivityMax.policyValue as number;

    const ransomwareFeatureMask = settings.find(s => s.name.toLowerCase() === 'ransomwarefeaturemask');
    this.ransomwareFeatureMaskDefault = ransomwareFeatureMask.value as ransomwareFeatureMaskValues;
    this.ransomwareFeatureMaskMin = ransomwareFeatureMask.min as ransomwareFeatureMaskValues;
    this.ransomwareFeatureMaskMax = ransomwareFeatureMask.max as ransomwareFeatureMaskValues;
    this.ransomwareFeatureMaskRecommended = ransomwareFeatureMask.policyValue as ransomwareFeatureMaskValues;

    this.defaultPolicy = {
      disabledExts: [],
      protectedExts: [],
      lockedExts: [],
      disabledDirs: [],
      protectedDirs: [],
      lockedDirs: [],
      encryptedProtectedDirs: [],
      encryptedLockedDirs: [],
      deceptionFiles: [],
      protectedVolumes: [],
      syncInterval: this.syncIntervalDefault,
      syncIntervalMetered: this.syncIntervalMeteredDefault,
      licenseInterval: this.licenseIntervalDefault,
      licenseIntervalMetered: this.licenseIntervalMeteredDefault,
      forcePreAuthValues: this.selectedReAuthFrequency,
      preAuthFileAccessCount: this.preAuthFileAccessCountDefault,
      preAuthFileAccessTimeout: this.getTimeoutFromSliderValue(this.preAuthFileAccessTimeoutDefault),
      ransomwareSensitivityMin: this.ransomwareSensitivityMinDefault,
      ransomwareSensitivityMax: this.ransomwareSensitivityMaxDefault,
      ransomwareFeatureMask: this.selectedFeatureMask
    };

    this.getSensorTypes();
  }

  ngAfterViewInit(): void {
      if (this.ripple) {
        this.ripple.disabled = true;
      }
  }

  getFreqTimeoutLabel(value: number) {
    let label: string;
    switch (value) {
      case 1:
      case 2:
        label = this.translate.instant('MINUTES');
        break;
      case 3:
        label = this.translate.instant('HOUR');
        break;
      case 4:
      case 5:
        label = this.translate.instant('HOURS');
        break;
      default:
        label = this.translate.instant('PRE_AUTH_UNLIMITED');
        break;
    }
    return label;
  }

  getSensorTypes() {
    if (this.authService.isAuthenticated()) {
      this.d3eService.getSensorTypes()
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            const sensorTypes = [];
            res.sensorTypes.forEach(sensorType => {
              if (this.DISPLAY_SENSORS.includes(sensorType.value)) {
                const sensor: ISensorType = {
                  value: sensorType.value,
                  displayValue: sensorType.displayValue,
                  description: sensorType.description,
                  hidden: sensorType.hidden,
                };
                sensorTypes.push(sensor);
              }

            });
            this.sensorTypes = sensorTypes;
          },
          error: () => {
            this.notification.showError(this.fUnknownError);
          }
      });
    }
  }

  getSensorToggleState(sensorType: number) {
    // Local (Default)
    let state = `2-${sensorType}`;
    // If sensor is saved in policy that means it is Off or On.
    if (this._d3ePolicy && this._d3ePolicy.sensors) {
      this._d3ePolicy.sensors.forEach(sensor => {
        if (sensor.sensorType === sensorType) {
          const isEnabled = sensor.enabled ? 1 : 0;
          state = isEnabled + `-${sensorType}`;
          return state;
        }
      });
    }
    return state;
  }

  onSensorToggleChanged($event, sensorType: number) {
    let sensorFound = false;
    if (this._d3ePolicy.sensors) {
      this._d3ePolicy.sensors.forEach(sensor => {
        if (sensor.sensorType === sensorType) {
          switch ($event.value) {
            // Local
            case `2-${sensorType}`:
              this._d3ePolicy.sensors.splice(this._d3ePolicy.sensors.indexOf(sensor), 1);
              break;
            // On
            case `1-${sensorType}`:
              sensor.enabled = true;
              break;
            // Off
            case `0-${sensorType}`:
              sensor.enabled = false;
              break;
          }
          sensorFound = true;
        }
      });
    }
    // Add the sensor to the array if it wasn't found. (Is new or was set to Local.)
    if (!sensorFound && ($event.value !== `2-${sensorType}`)) {
      let enabled: boolean;
      switch ($event.value) {
        // On
        case `1-${sensorType}`:
          enabled = true;
          break;
        // Off
        case `0-${sensorType}`:
          enabled = false;
          break;
      }
      if (enabled !== undefined) {
        const newSensor: ISensor = {
          sensorType: sensorType,
          enabled: enabled
        };
        if (this._d3ePolicy.sensors === undefined) {
          this._d3ePolicy.sensors = [];
        }
        this._d3ePolicy.sensors.push(newSensor);
      }
    }
    this.sensorToggleChanged.emit(this._d3ePolicy);
  }

  onSelectionImportChanged(device: IDevice) {
    this.selectedDevice = device;
  }

  onFileFrequencyChanged(value: number) {
    if (value !== this.selectedFileFrequency) {
      this.selectedFileFrequency = value;
      this._d3ePolicy.preAuthFileAccessCount = this.selectedFileFrequency;
      this.preAuthFileAccessCountChanged.emit(this._d3ePolicy);
    }
  }

  onSelectionTimeoutChanged(value: number) {
    if (this.selectedTimeout !== value) {
      this.selectedTimeout = value;
      this._d3ePolicy.preAuthFileAccessTimeout = this.getTimeoutFromSliderValue(this.selectedTimeout);
      this.preAuthFileAccessTimeoutChanged.emit(this._d3ePolicy);
    }
  }

  getSliderValueFromTimeout(timeout: validTimeoutValues): number {
    let value: number;
    switch (timeout) {
      case 10:
        value = 1;
        break;
      case 30:
        value = 2;
        break;
      case 60:
        value = 3;
        break;
      case 240:
        value = 4;
        break;
      case 480:
        value = 5;
        break;
      default:
        value = 6;
        break;
    }

    return value;
  }

  getTimeoutFromSliderValue(value: number): validTimeoutValues {
    let timeout: validTimeoutValues;
    switch (value) {
      case 1:
        timeout = 10;
        break;
      case 2:
        timeout = 30;
        break;
      case 3:
        timeout = 60;
        break;
      case 4:
        timeout = 240;
        break;
      case 5:
        timeout = 480;
        break;
      default:
        timeout = 0;
        break;
    }

    return timeout;
  }

  getTimeValueFromTimeout(timeout: number) {
    let value: number;
    switch (timeout) {
      case 1:
        value = 10;
        break;
      case 2:
        value = 30;
        break;
      case 3:
        value = 1;
        break;
      case 4:
        value = 4;
        break;
      case 5:
        value = 8;
        break;
      default:
        value = 0;
        break;
    }

    return value;
  }

  onLoad() {
    this.loadDeviceSettings.emit(this.selectedDevice);
  }

  updatePolicy(policy: ID3EPolicy) {
    this._d3ePolicy = Object.assign({}, this.defaultPolicy, policy);


    this._d3ePolicy.syncInterval = this._d3ePolicy.syncInterval != null ?
                                  this._d3ePolicy.syncInterval :
                                  this.convertToTimeUnits(this.syncIntervalDefault, this.getTimeUnitsFromTime(this.syncIntervalDefault));
    this.syncIntervalUnit = this.getTimeUnitsFromTime(this._d3ePolicy.syncInterval);

    this._d3ePolicy.syncIntervalMetered = this._d3ePolicy.syncIntervalMetered != null ?
                                          this._d3ePolicy.syncIntervalMetered :
                                          this.convertToTimeUnits(this.syncIntervalMeteredDefault, this.getMeteredTimeUnitsFromTime(this.syncIntervalMeteredDefault));
    this.syncIntervalMeteredUnit = this.getMeteredTimeUnitsFromTime(this._d3ePolicy.syncIntervalMetered);

    this._d3ePolicy.licenseInterval = this._d3ePolicy.licenseInterval != null ?
                                      this._d3ePolicy.licenseInterval :
                                      this.convertToTimeUnits(this.licenseIntervalDefault, 'hrs');

    this._d3ePolicy.licenseIntervalMetered = this._d3ePolicy.licenseIntervalMetered != null ?
                                            this._d3ePolicy.licenseIntervalMetered :
                                            this.convertToTimeUnits(this.licenseIntervalMeteredDefault, 'hrs');

    this.selectedReAuthFrequency = this._d3ePolicy.forcePreAuthValues != null ?
      this._d3ePolicy.forcePreAuthValues : this.forcePreAuthValuesDefault;

    this.selectedFileFrequency = this._d3ePolicy.preAuthFileAccessCount != null ?
      this._d3ePolicy.preAuthFileAccessCount : this.preAuthFileAccessCountDefault;

    this.selectedTimeout = this._d3ePolicy.preAuthFileAccessTimeout != null ?
      this.getSliderValueFromTimeout(this._d3ePolicy.preAuthFileAccessTimeout) :
      this.getSliderValueFromTimeout(this.preAuthFileAccessTimeoutDefault);

    this.selectedFeatureMask = this._d3ePolicy.ransomwareFeatureMask != null ?
        this._d3ePolicy.ransomwareFeatureMask : this.ransomwareFeatureMaskDefault;

    this._d3ePolicy.ransomwareSensitivityMin = this._d3ePolicy.ransomwareSensitivityMin != null ?
                this._d3ePolicy.ransomwareSensitivityMin : this.ransomwareSensitivityMinDefault;

    this._d3ePolicy.ransomwareSensitivityMax = this._d3ePolicy.ransomwareSensitivityMax != null ?
                this._d3ePolicy.ransomwareSensitivityMax : this.ransomwareSensitivityMaxDefault;
  }

  convertToTimeUnits(value: number, unit: 'secs' | 'mins' | 'hrs'): number {
    let time = 1000;
    switch (unit) {
      case 'secs':
        time = value * 1000;
        break;
      case 'mins':
        time = value * 1000 * 60;
        break;
      case 'hrs':
        time = value * 1000 * 60 * 60;
        break;
    }

    return time;
  }

  convertFromTimeUnits(time: number, unit: 'secs' | 'mins' | 'hrs'): number {
    let value = 1;
    switch (unit) {
      case 'secs':
        value = time / 1000;
        break;
      case 'mins':
        value = time / 1000 / 60;
        break;
      case 'hrs':
        value = time / 1000 / 60 / 60;
        break;
    }

    return value;
  }

  getTimeUnitsFromTime(time: number): 'secs' | 'mins' | 'hrs' {
    let timeUnit: 'secs' | 'mins' | 'hrs';
    if (time <= 60000) {
      timeUnit = 'secs';
      this.timeValues = this.seconds;
    } else if (time > 60000 && time < 3600000) {
      timeUnit = 'mins';
      this.timeValues = this.minutes;
    } else if (time >= 3600000) {
      timeUnit = 'hrs';
      this.timeValues = this.hours;
    }
    return timeUnit;
  }

  getMeteredTimeUnitsFromTime(time: number): 'mins' | 'hrs' {
    let timeUnit: 'mins' | 'hrs';
    if (time < 3600000) {
      timeUnit = 'mins';
      this.timeValuesMetered = this.minutesMetered;
    } else if (time >= 3600000) {
      timeUnit = 'hrs';
      this.timeValuesMetered = this.hoursMetered;
    }
    return timeUnit;
  }

  onPolicyToggleChange(checked: boolean) {
    this.forcePolicyRemoval = checked;
    this.forcePolicyRemovalChanged.emit(this.forcePolicyRemoval);
  }

  onAlwaysOnToggleChange(checked: boolean) {
    if (this.allowAlwaysOn) {
      if (this._d3ePolicy.lockedExts.length > 0) {
        const lockedExtensions = this._d3ePolicy.lockedExts.join(', ');

        const vals = this.translate.instant(['ALERT_OK', 'ALWAYS_ON_FOUND_TITLE', 'ALWAYS_ON_FOUND_CONTENT'], {
          lockedExtensions: lockedExtensions
        });
        const title = vals['ALWAYS_ON_FOUND_TITLE'];
        const content = vals['ALWAYS_ON_FOUND_CONTENT'];
        const dialogRef = this.dialog.open(AlertDialogComponent, {
          width: '450px',
          data: {
            title,
            content,
            acceptBtnName: vals['ALERT_OK'],
            showRejectBtn: false,
          },
        });

        dialogRef.afterClosed().subscribe(() => {
          this.ripple.launch({
            centered: true,
            animation: {
              enterDuration: 500,
              exitDuration: 1000
            }
          });
        });
        this.allowAlwaysOn = true;
        this.ref.checked = true;
      } else {
        this.allowAlwaysOn = false;
        this.allowAlwaysOnChanged.emit(false);
      }
    } else {
      this.allowAlwaysOn = checked;
      this.allowAlwaysOnChanged.emit(checked);
    }
  }

  getSyncInterval() {
    if (this._d3ePolicy && this._d3ePolicy.syncInterval) {
      return this.convertFromTimeUnits(this._d3ePolicy.syncInterval, this.syncIntervalUnit);
    } else {
      return this.syncIntervalDefault;
    }
  }

  getSyncIntervalMetered() {
    if (this._d3ePolicy && this._d3ePolicy.syncIntervalMetered) {
      return this.convertFromTimeUnits(this._d3ePolicy.syncIntervalMetered, this.syncIntervalMeteredUnit);
    } else {
      return this.syncIntervalMeteredDefault;
    }
  }

  onSyncChanged(value) {
    const syncTime = this.convertToTimeUnits(value, this.syncIntervalUnit);

    this._d3ePolicy.syncInterval = syncTime;
    if (this._d3ePolicy.syncInterval !== syncTime) {
      this.intervalsChanged.emit(this._d3ePolicy);
    }
  }

  onSyncMeteredChanged(value) {
    const syncTime = this.convertToTimeUnits(value, 'mins');

    if (this._d3ePolicy.syncIntervalMetered !== syncTime) {
      this._d3ePolicy.syncIntervalMetered = syncTime;
      this.intervalsChanged.emit(this._d3ePolicy);
    }
  }

  onSliderMinValueChange(value) {
    this._d3ePolicy.ransomwareSensitivityMin = value;
    this.ransomwareSensitivityMinChanged.emit(this._d3ePolicy);
  }

  onSliderHighValueChange(value) {
    this._d3ePolicy.ransomwareSensitivityMax = value;
    this.ransomwareSensitivityMaxChanged.emit(this._d3ePolicy);
  }

  onRansomwareFeatureMaskChange(value) {
      this.selectedFeatureMask = value;
      if ( this._d3ePolicy.ransomwareFeatureMask !== this.selectedFeatureMask ) {
        this._d3ePolicy.ransomwareFeatureMask = this.selectedFeatureMask;
        this.ransomwareFeatureMaskChanged.emit(this._d3ePolicy);
      }
  }

  onReAuthFrequencyChange(value: validReAuthFrequencyValues) {
    this.selectedReAuthFrequency = value;
    if (this._d3ePolicy.forcePreAuthValues !== this.selectedReAuthFrequency) {
      this._d3ePolicy.forcePreAuthValues = this.selectedReAuthFrequency;
      this.forcePreAuthChanged.emit(this._d3ePolicy);
    }
  }

  onSetRecommendedClick() {
    if (this._d3ePolicy.syncInterval !== this.syncIntervalRecommended ||
      this._d3ePolicy.syncIntervalMetered !== this.syncIntervalMeteredRecommended ||
      this._d3ePolicy?.preAuthFileAccessCount !== this.preAuthFileAccessCountRecommended ||
      this._d3ePolicy?.ransomwareSensitivityMin !== this.ransomwareSensitivityMinRecommended ||
      this._d3ePolicy?.ransomwareSensitivityMax !== this.ransomwareSensitivityMaxRecommended ||
      this._d3ePolicy?.ransomwareFeatureMask !== this.ransomwareFeatureMaskRecommended ||
      this.allowAlwaysOn !== false) {
        this._d3ePolicy.syncInterval = this.syncIntervalRecommended;
        this.syncIntervalUnit = this.getTimeUnitsFromTime(this.syncIntervalRecommended);
        this._d3ePolicy.syncIntervalMetered = this.syncIntervalMeteredRecommended;
        this.syncIntervalMeteredUnit = this.getMeteredTimeUnitsFromTime(this.syncIntervalMeteredRecommended);
        this._d3ePolicy.preAuthFileAccessCount = this.preAuthFileAccessCountRecommended;
        this._d3ePolicy.ransomwareSensitivityMin = this.ransomwareSensitivityMinRecommended;
        this._d3ePolicy.ransomwareSensitivityMax = this.ransomwareSensitivityMaxRecommended;
        this._d3ePolicy.ransomwareFeatureMask = this.ransomwareFeatureMaskRecommended as ransomwareFeatureMaskValues;
        // TODO: Get default setting for this?
        this.onAlwaysOnToggleChange(false);
        // The onSelectionTimeoutChanged() function will call emit(this._d3ePolicy)
        this.onSelectionTimeoutChanged(this.preAuthFileAccessTimeoutRecommended);
    }

  }
  onSyncIntervalChanged(value) {
    const syncTime = this.convertToTimeUnits(value, this.syncIntervalUnit);

    if (this._d3ePolicy.syncInterval !== syncTime) {
      this._d3ePolicy.syncInterval = syncTime;
      this.intervalsChanged.emit(this._d3ePolicy);
    }
  }

  onSyncIntervalUnitChanged(value) {
    let syncInterval = this.convertFromTimeUnits(this._d3ePolicy.syncInterval, this.syncIntervalUnit);

    switch (value) {
      case 'secs':
        this.timeValues = this.seconds;
        break;
      case 'mins':
        this.timeValues = this.minutes;
        break;
      case 'hrs':
        this.timeValues = this.hours;
        break;
    }

    if (syncInterval < this.timeValues[0] || syncInterval > this.timeValues[this.timeValues.length - 1]) {
      syncInterval = this.timeValues[this.timeValues.length - 1];
    }

    this._d3ePolicy.syncInterval = this.convertToTimeUnits(syncInterval, value);
    if (this.syncIntervalUnit !== value) {
      this.syncIntervalUnit = value;
      this.intervalsChanged.emit(this._d3ePolicy);
    }
  }

  onSyncIntervalMeteredChanged(value) {
    const syncTime = this.convertToTimeUnits(value, this.syncIntervalMeteredUnit);

    if (this._d3ePolicy.syncIntervalMetered !== syncTime) {
      this._d3ePolicy.syncIntervalMetered = syncTime;
      this.intervalsChanged.emit(this._d3ePolicy);
    }
  }

  onSyncIntervalMeteredUnitChanged(value) {
    let syncInterval = this.convertFromTimeUnits(this._d3ePolicy.syncIntervalMetered, this.syncIntervalMeteredUnit);

    switch (value) {
      case 'mins':
        this.timeValuesMetered = this.minutesMetered;
        break;
      case 'hrs':
        this.timeValuesMetered = this.hoursMetered;
        break;
    }

    if (syncInterval < this.timeValuesMetered[0] || syncInterval > this.timeValuesMetered[this.timeValuesMetered.length - 1]) {
      syncInterval = this.timeValuesMetered[this.timeValuesMetered.length - 1];
    }

    this._d3ePolicy.syncIntervalMetered = this.convertToTimeUnits(syncInterval, value);
    if (this.syncIntervalMeteredUnit !== value) {
      this.syncIntervalMeteredUnit = value;
      this.intervalsChanged.emit(this._d3ePolicy);
    }
  }

}
